"use client";

import { useRef } from "react";
import { motion, useInView } from "@/lib/motion";
import { GREEN } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useDeviceShade } from "./device-shade";

// Cellular signal bars, shortest to tallest — they pulse upward in sequence.
const SIGNAL_BARS = [{ h: 14 }, { h: 21 }, { h: 28 }, { h: 35 }];

interface Props {
  reduced: boolean;
  hovered: boolean;
}

export function MobileProxiesVisual({ reduced, hovered }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { margin: "100px 0px" });
  const active = !reduced && inView;
  const shade = useDeviceShade();

  return (
    <div ref={rootRef} className="relative h-full w-full overflow-hidden">
      {/* Blueprint dotted grid */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(color-mix(in srgb, var(--foreground) 6%, transparent) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* Ambient glow behind the stack */}
      <div
        className={cn(
          "pointer-events-none absolute top-1/2 left-1/2 mt-[-70px] ml-[-110px] h-[150px] w-[220px] rounded-full blur-3xl transition-opacity duration-500",
          hovered ? "opacity-90" : "opacity-50"
        )}
        style={{ background: `radial-gradient(closest-side, ${GREEN}1A, transparent)` }}
      />

      {/* 3D stage — isometric phone layer, centered */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ perspective: 1100 }}
      >
        <div
          className={cn(
            "relative mt-3 h-[180px] w-[100px]",
            !reduced && "transition-transform duration-500"
          )}
          style={{
            transformStyle: "preserve-3d",
            transform: hovered
              ? "rotateX(54deg) rotateZ(-34deg)"
              : "rotateX(57deg) rotateZ(-38deg)",
          }}
        >
          {/* Ghost slab — dashed wireframe box at the bottom of the stack */}
          <motion.div className="absolute inset-0" style={{ transformStyle: "preserve-3d" }}>
            <div
              className="border-foreground/20 absolute inset-0 rounded-[22px] border border-dashed"
              style={{ transform: "translateZ(-26px)" }}
            />
            <div
              className="border-foreground/10 absolute inset-0 rounded-[22px] border border-dashed"
              style={{ transform: "translateZ(-44px)" }}
            />
          </motion.div>

          {/* Phone layer — a 3D slab with thickness, edge light, and shadow */}
          <motion.div
            className="absolute inset-0"
            style={{ transformStyle: "preserve-3d" }}
            initial={{ z: 0 }}
            animate={active ? { z: [0, 2.5, 0, -2.5, 0] } : undefined}
            transition={{ duration: 4.4, repeat: Infinity, ease: "easeInOut" }}
          >
            {/* Drop shadow beneath the slab */}
            <div
              className="absolute -inset-2 rounded-[26px] bg-black/50 blur-lg"
              style={{ transform: "translateZ(-15px)" }}
            />
            {/* Slab underside */}
            <div
              className="absolute inset-0 rounded-[16px]"
              style={{ transform: "translateZ(-10px)", backgroundColor: shade("#1a1d21") }}
            />
            {/* Side walls — real extruded thickness along the front edges */}
            <div
              className="absolute right-[16px] bottom-0 left-[16px] h-[10px]"
              style={{
                transformOrigin: "bottom",
                transform: "rotateX(90deg)",
                background: `linear-gradient(180deg, ${shade("#3a3e44")} 0%, ${shade("#1e2025")} 100%)`,
              }}
            >
              {/* Charging port */}
              <div className="absolute top-1/2 left-1/2 h-[4px] w-[14px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/25 bg-black/75" />
              {/* Speaker holes */}
              <div className="absolute top-1/2 right-2 flex -translate-y-1/2 gap-[2.5px]">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={`hole-r-${i + 1}`}
                    className="h-[2.5px] w-[2.5px] rounded-full bg-black/80"
                  />
                ))}
              </div>
              {/* Mic holes */}
              <div className="absolute top-1/2 left-2 flex -translate-y-1/2 gap-[2.5px]">
                {[0, 1].map((i) => (
                  <span
                    key={`hole-l-${i + 1}`}
                    className="h-[2.5px] w-[2.5px] rounded-full bg-black/80"
                  />
                ))}
              </div>
            </div>
            <div
              className="absolute top-[16px] right-0 bottom-[16px] w-[10px]"
              style={{
                transformOrigin: "right",
                transform: "rotateY(-90deg)",
                background: `linear-gradient(90deg, ${shade("#2a2e33")} 0%, ${shade("#16181c")} 100%)`,
              }}
            />
            {/* Top face */}
            <div
              className={cn(
                "absolute inset-0 overflow-hidden rounded-[16px] border border-t-white/30 transition-colors duration-300",
                hovered ? "border-white/30" : "border-white/20"
              )}
              style={{ backgroundColor: shade("#0A0B0D") }}
            >
              <div className="absolute inset-0" style={{ backgroundColor: `${GREEN}0D` }} />
              {/* Directional light across the face */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, rgba(247,247,248,0.06), transparent 45%)",
                }}
              />

              {/* Earpiece speaker + front camera */}
              <div className="absolute top-[9px] left-1/2 h-[4px] w-7 -translate-x-1/2 rounded-full border border-white/30 bg-black/85" />
              <div className="absolute top-[8px] left-[calc(50%+20px)] h-[6px] w-[6px] rounded-full border border-white/35 bg-black/90">
                <div className="absolute top-[1.5px] left-[1.5px] h-[2px] w-[2px] rounded-full bg-white/60" />
              </div>

              {/* Mic / speaker holes at the bottom edge */}
              <div className="absolute bottom-[9px] left-1/2 flex -translate-x-1/2 gap-[3px]">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={`mic-${i + 1}`}
                    className="h-[2.5px] w-[2.5px] rounded-full bg-white/45"
                  />
                ))}
              </div>

              {/* Mobile signal — bars pulse upward in sequence */}
              <div className="absolute top-[26px] left-1/2 flex -translate-x-1/2 items-end gap-[5px]">
                {SIGNAL_BARS.map((bar, i) => (
                  <motion.span
                    key={bar.h}
                    className="w-[8px] rounded-[2.5px]"
                    style={{ height: bar.h, backgroundColor: GREEN }}
                    initial={{ opacity: reduced ? 0.9 : 0.25 }}
                    animate={active ? { opacity: [0.25, 1, 0.25] } : undefined}
                    transition={{
                      duration: 2.4,
                      delay: i * 0.3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                ))}
              </div>

              {/* Readout */}
              <div className="absolute inset-x-2 top-[92px] flex flex-col items-center gap-1 text-center">
                <span className="block font-mono text-[7.5px] tracking-[0.14em] text-white">
                  SUCCESS RATE
                </span>
                <span
                  className="block text-[17px] leading-none font-semibold"
                  style={{ color: GREEN }}
                >
                  99.5%+
                </span>
              </div>
            </div>

            {/* Volume rocker + power button on the right edge */}
            <div
              className="absolute top-[34px] right-[-2px] h-5 w-[3px] rounded-r-full"
              style={{ backgroundColor: shade("#454a51") }}
            />
            <div
              className="absolute top-[58px] right-[-2px] h-7 w-[3px] rounded-r-full"
              style={{ backgroundColor: shade("#454a51") }}
            />
            {/* Soft breathing glow, twice the bar cadence */}
            {!reduced && (
              <motion.div
                className="pointer-events-none absolute inset-0 rounded-[16px] border"
                style={{
                  borderColor: `${GREEN}59`,
                  boxShadow: `0 0 18px ${GREEN}2E, inset 0 0 14px ${GREEN}17`,
                }}
                initial={{ opacity: 0 }}
                animate={active ? { opacity: [0, 0.6, 0] } : undefined}
                transition={{ duration: 4.8, delay: 0.9, repeat: Infinity, ease: "easeInOut" }}
              />
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
